import React from 'react';
import { Close } from '@mui/icons-material';
import { Box, Dialog, IconButton, styled } from '@mui/material';
import { Media } from '@askporter/utils';

// Styled components for image preview dialog
const ImageHeader = styled(Box)({
  zIndex: 2,
  background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  color: '#FFFFFF',
});

const ImageBackground = styled(Box)({
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundPosition: 'center center',
  position: 'absolute',
  top: '-10px',
  bottom: '-10px',
  left: '-10px',
  right: '-10px',
  filter: 'blur(6px) brightness(0.4)',
  zIndex: 0,
});

/** Wraps the central image/video only; stops propagation so clicking the media doesn't close. */
const ImageContainer = styled(Box)({
  display: 'inline-block',
  zIndex: 1,
  maxWidth: '70vw',
  img: {
    objectFit: 'contain',
    display: 'block',
  },
});

export interface PreviewImageProps {
  /**
   * The image to preview
   */
  image: Media | undefined;
  /**
   * Callback to close the preview
   */
  onClose: () => void;
}

/**
 * PreviewImage displays a full-screen dialog with an image or video preview
 * Features a blurred background, gradient header, and close button
 */
export const PreviewImage: React.FC<PreviewImageProps> = ({ image, onClose }) => {
  const isVideo = image?.nameAndType?.type === 'mp4' || image?.nameAndType?.type === 'hevc';
  const mediaSrc = image?.path || image?.tempPath;
  const mediaType = isVideo ? 'video' : 'image';
  const fileName = image?.nameAndType?.name || `Media ${mediaType}`;

  const handleClose = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onClose();
  };

  return (
    <Dialog
      fullScreen
      open={!!image}
      onClose={onClose}
      aria-labelledby="media-preview-title"
      aria-describedby="media-preview-description"
      slotProps={{
        paper: {
          sx: {
            overflow: 'hidden',
            backgroundColor: 'transparent',
          },
        },
      }}
    >
      <Box id="media-preview-title" sx={{ position: 'absolute', left: '-9999px' }}>
        {`${fileName} preview`}
      </Box>
      <Box id="media-preview-description" sx={{ position: 'absolute', left: '-9999px' }}>
        {isVideo ? 'Video preview with playback controls' : 'Full screen image preview'}
      </Box>
      {/* Blurred background first so it sits behind the clickable overlay */}
      <ImageBackground aria-hidden="true" sx={{ backgroundImage: `url(${mediaSrc || ''})` }} />
      {/* Full-screen clickable overlay on top: click anywhere outside the main image to close */}
      <Box
        onClick={onClose}
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          cursor: 'default',
        }}
      >
        <ImageHeader>
          <IconButton
            onClick={handleClose}
            aria-label={`Close ${fileName} preview`}
            sx={{
              padding: '16px',
              color: '#ffffff',
              margin: '6px',
            }}
          >
            <Close />
          </IconButton>
        </ImageHeader>
        {/* Center area: fills remaining space so clicks on blurred background close */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 0,
          }}
        >
          {/* Only the central image/video stops propagation; clicks here do not close */}
          <ImageContainer onClick={(e) => e.stopPropagation()} sx={{ cursor: 'default' }}>
            {isVideo ? (
              <video
                controls
                autoPlay
                aria-label={`${fileName} video player`}
                style={{ maxWidth: '70vw', maxHeight: '80vh', display: 'block' }}
                src={mediaSrc}
              >
                <track kind="captions" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <img style={{ maxWidth: '70vw', display: 'block' }} alt={fileName} src={mediaSrc} />
            )}
          </ImageContainer>
        </Box>
      </Box>
    </Dialog>
  );
};
